/**
 * One row of the commit ledger.
 *
 * A level is a node on the rail, and the row is the commit line beside it.
 * Everything that moves here is trying to say something: the node lifts under
 * the cursor because it is reachable, the current level's node sheds a ring
 * every couple of seconds because that is where you are, and a locked row
 * refuses sideways because it is not your turn yet.
 */
import { motion, useAnimate, type Variants } from 'motion/react';
import type { MouseEvent } from 'react';
import type { PublicMission } from '../../types';
import type { PaidSpan } from '../../lib/entitlement';
import { Machine, Parts, Stars } from '../../ui';
import { rowVariants, spring } from '../../lib/motion';
import { num } from '../../lib/format';
import { useStrings } from '../../state/useLocale';
import { sound } from '../../lib/sound';

/* The row carries the entrance variants from lib/motion plus a hover state.
   `hover` is a label, so it propagates through the plain <a> to the node. */
const rowMotion: Variants = {
  ...rowVariants,
  hover: { x: 2, transition: spring },
};

const nodeMotion: Variants = {
  hover: { scale: 1.15, transition: spring },
};

/* The pulse. It starts life exactly on top of the ring the stylesheet already
   draws around the current node (11px dot + 4px shadow ring = 19px across),
   so it reads as that ring shedding a copy of itself rather than as a new
   object appearing. Expansion takes under half the cycle; the rest is
   silence, which is what keeps it from feeling like a notification badge. */
const PULSE_PERIOD = 2.4;

export interface LevelRowProps {
  mission: PublicMission;
  /**
   * This row is a checkpoint — the assessment at the foot of a world, drawn
   * inside that world's section (components/home/Home.tsx, `buildWorlds`).
   *
   * It is passed rather than read off `mission.track`, because the caller is
   * the one that decided where this row is being drawn. A checkpoint the map
   * could not place keeps a section of its own and is still a checkpoint; the
   * day something else wants a row like this, it will not have to be given a
   * track to get one.
   *
   * What it changes is small and entirely presentational. The gate, the
   * stars, the XP and every refusal are computed the same way for both kinds
   * of row and arrive here already decided.
   */
  checkpoint?: boolean;
  stars: number;
  cleared: boolean;
  /** Cleared, but the clear could not be vouched for. Stars draw hollow until a plausible replay. */
  unverified: boolean;
  /** Why, in the words progress recorded; shown as the label's tooltip so the reason is never hidden from the learner. */
  unverifiedWhy?: string;
  /**
   * Passed over without being cleared. A state of its own on the map,
   * because a learner who left a level behind is owed the difference between
   * "not reached yet" and "reached, and still waiting for you".
   */
  skipped?: boolean;
  locked: boolean;
  /**
   * Locked because it has not been bought, rather than because the level
   * before it is not cleared. A different refusal with a different fix, so a
   * different row: it does not shake, because shaking reads as "wrong" and
   * there is nothing wrong with not having bought something yet.
   *
   * Optional and false by default, so the row renders exactly as it did
   * before anything passes it.
   */
  paywalled?: boolean;
  /**
   * The paid half as the catalogue reports it: how many levels it is, and
   * where they run when that can be said at all. Only the "full course" chip
   * reads it, and only to say what buying would open.
   *
   * It is passed in rather than worked out here because a row is handed one
   * mission and has no catalogue to read: the ledger knows the whole list and
   * computes this once for every row (lib/entitlement's `paidSpan`). Optional
   * and null by default for the same reason `paywalled` is false by default,
   * and the chip simply goes without its tooltip when it is missing — which
   * cannot happen from the ledger, since a row is only paywalled when
   * something is locked and something locked is what makes the span.
   *
   * ## Why the row checks the track before quoting the range
   *
   * This used to be `{ from, to }` — two numbers, which the row printed. Two
   * numbers are only a sentence about *this* row if they are this row's
   * lane's numbers: level 20 of the backend track and level 20 of the general
   * track are different levels with the same number (docs/curriculum.md §5),
   * and a tooltip on a backend row quoting the general track's `20-51` is
   * naming rows the reader cannot see. So the span carries the track it
   * belongs to and the chip quotes the range only when it is this mission's.
   *
   * The alternative was to give the row its own lane's scope instead of the
   * catalogue's span. That answer already exists — `paidScope` in
   * lib/entitlement — and it is deliberately private there, because it is the
   * *refusal's* answer: it reaches the learner through `lockedMessage`, in
   * the toast this very chip produces when it is clicked. Reproducing it out
   * here would be a second definition of which levels a lane sells, in a
   * component, for a sentence that lives in a `title` attribute.
   *
   * What the span can say without it is a shade coarser and never wrong. When
   * the paid half is one lane's tail — the shape the course ships in — the
   * range is that lane's and the tooltip and the toast quote the same two
   * numbers. When it is not, the tooltip counts while the toast, which knows
   * the row's lane, may still be able to name a range in it. A tooltip that
   * says less than the sentence a click produces is the right way round for
   * the two to differ.
   */
  paidSpan?: PaidSpan | null;
  current: boolean;
  /** Already formatted: "150 XP" when cleared, "+150 XP" when not. */
  xpLabel: string;
  /** True when the viewer asked for reduced motion; everything renders settled. */
  reduced: boolean;
  /**
   * The id, not the level number it used to be: `level` is a position within
   * a track and stops identifying a mission the day a second track ships, and
   * the shell needs the mission itself to say which refusal this is
   * (components/contracts.ts, lib/entitlement.ts).
   */
  onLocked(missionId: string): void;
}

export function LevelRow({
  mission,
  checkpoint = false,
  stars,
  cleared,
  unverified,
  unverifiedWhy,
  skipped = false,
  locked,
  paywalled = false,
  paidSpan = null,
  current,
  xpLabel,
  reduced,
  onLocked,
}: LevelRowProps) {
  const s = useStrings();
  const [scope, animate] = useAnimate<HTMLLIElement>();

  // A paywalled row is a locked row whatever the caller passed for `locked`:
  // there is no level behind the card to open, so the href and the click
  // refusal have to hold on this alone.
  const blocked = locked || paywalled;
  const state = cleared ? 'is-cleared' : blocked ? 'is-locked' : current ? 'is-current' : 'is-open';
  // Unverified is a modifier on cleared, not a state of its own: the row is
  // done and the node is filled; only the stars go hollow and say why.
  //
  // Paywalled is a modifier on locked for the same reason, and it is the
  // reason the base state above collapses both kinds of refusal into
  // `is-locked`. The stylesheet says "not openable" in one place — dimmed
  // text, dashed node, no hover — through selectors written as
  // `.lvl:not(.is-locked)`. A paywalled row that carried only its own class
  // would slip through every one of them and light up under the cursor like
  // a level you could play.
  const hollow = cleared && unverified;
  // Skipped is a modifier too, on an open row: the level is playable, it is
  // simply behind the learner. Nothing about it is a refusal, so it changes
  // no state and takes no colour.
  const passedOver = skipped && !cleared;
  // Checkpoint is a modifier too, and on any of the states above: an
  // assessment can be locked, open, current or cleared exactly as a level
  // can. It adds the separating rule and the node's shape and takes nothing
  // away, which is why it is last and why it never replaces `state` — the
  // stylesheet's "not openable" selectors are written `.lvl:not(.is-locked)`
  // and a row that carried only its own class would slip through them all.
  const className = [
    'lvl',
    state,
    hollow ? 'is-unverified' : '',
    paywalled ? 'is-paywalled' : '',
    passedOver ? 'is-skipped' : '',
    checkpoint ? 'is-checkpoint' : '',
  ]
    .filter(Boolean)
    .join(' ');

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (!blocked) return;
    event.preventDefault();
    sound.click();
    // Two oscillations of about four pixels: enough to read as "no", short
    // enough that it never delays the explanation the shell is about to show.
    //
    // Not for a paywalled row. The shake is the gesture for "not yet, it is
    // not your turn", and a level you have not bought is not a mistake you
    // made — it is a thing on sale. Refusing it in the same body language as
    // a wrong click would make an offer feel like a scolding.
    if (!reduced && !paywalled && scope.current) {
      void animate(scope.current, { x: [0, -4, 4, -4, 4, 0] }, { duration: 0.22, ease: 'easeInOut' });
    }
    onLocked(mission.id);
  }

  // The chip's tooltip, and the one decision in it: a range is quotable here
  // only when it is this mission's lane's range. Otherwise the sentence
  // counts, which is true of the paid half whatever lanes it is spread over.
  const paidTitle = !paidSpan
    ? undefined
    : paidSpan.range && paidSpan.range.track === mission.track
      ? s.home.row.paidWhy(paidSpan.range.from, paidSpan.range.to)
      : s.home.row.paidWhyCount(paidSpan.count);

  return (
    <motion.li
      ref={scope}
      className={className}
      variants={reduced ? undefined : rowMotion}
      whileHover={reduced || blocked ? undefined : 'hover'}
    >
      <a
        className="lvl-link"
        href={blocked ? '#' : `?mission=${encodeURIComponent(mission.id)}`}
        data-level={mission.level}
        data-locked={blocked ? '1' : '0'}
        data-paywalled={paywalled ? '1' : undefined}
        data-checkpoint={checkpoint ? '1' : undefined}
        aria-label={
          checkpoint
            ? paywalled
              ? s.home.row.labelCheckpointPaid(mission.title)
              : s.home.row.labelCheckpoint(mission.title)
            : paywalled
              ? s.home.row.labelPaid(mission.level, mission.title)
              : s.home.row.label(mission.level, mission.title)
        }
        aria-disabled={blocked || undefined}
        onClick={handleClick}
      >
        <motion.span className="lvl-node" aria-hidden="true" variants={reduced || blocked ? undefined : nodeMotion} />

        {current && !reduced && (
          <motion.span
            aria-hidden="true"
            style={{
              position: 'absolute',
              // Logical, so the ring follows the rail when the page turns
              // around — and 21 is the rail: it has to stay in lockstep with
              // `.levels::before { inset-inline-start: 21px }` in home.css,
              // which is the line these nodes sit on.
              insetInlineStart: 21,
              top: '50%',
              width: 19,
              height: 19,
              marginInlineStart: -9.5,
              marginTop: -9.5,
              boxSizing: 'border-box',
              borderRadius: 999,
              border: '1px solid var(--ink-max)',
              pointerEvents: 'none',
            }}
            initial={{ scale: 1, opacity: 0.5 }}
            animate={{ scale: [1, 2.1, 2.1], opacity: [0.5, 0, 0] }}
            transition={{
              duration: PULSE_PERIOD,
              times: [0, 0.45, 1],
              ease: 'easeOut',
              repeat: Infinity,
              // Let the row finish arriving before the ledger starts breathing.
              delay: 0.6,
            }}
          />
        )}

        {/* A checkpoint has a number — it is a track of its own, numbered
            0..n-1 — and printing it here would be a lie told in the one
            column the reader trusts to be a position. Recovery's levels run
            10, 11, 12, and the checkpoint under them is the third on the
            checkpoint track, so this cell would read "02" at the foot of a
            chapter that had just counted past twelve.

            A mark instead, and aria-hidden: the label on the anchor already
            says "Checkpoint: Recovery", so there is nothing here for a screen
            reader to gain and a lone "diamond" to lose. */}
        {checkpoint ? (
          <span className="lvl-num lvl-mark" aria-hidden="true">
            ◆
          </span>
        ) : (
          <span className="lvl-num">{num(mission.level).padStart(2, '0')}</span>
        )}
        <span className="lvl-name">
          {mission.title}
          {hollow && (
            <span className="lvl-unverified" title={s.home.row.unverifiedWhy(unverifiedWhy)}>
              {s.home.row.unverified}
            </span>
          )}
          {paywalled && (
            <span className="lvl-paid" title={paidTitle}>
              {s.home.row.paid}
            </span>
          )}
          {passedOver && (
            <span className="lvl-skipped" title={s.home.row.skippedWhy}>
              {s.home.row.skipped}
            </span>
          )}
        </span>
        {/* A slug — `shell-basics`, `git.reflog`. It is a label for the
            machine that happens to be legible (docs/i18n.md §6.1), so it is
            not translated and it is not left for bidi to rearrange. */}
        <Machine className="lvl-concept">{mission.concept}</Machine>
        <span className="lvl-time">{s.home.row.minutes(mission.estimated_minutes)}</span>
        <Stars value={stars} className="lvl-stars" />
        <span className="lvl-xp">{xpLabel}</span>
      </a>

      {/* The way back to the article, and the only one that lasts. The clear
          overlay offers it once and then dismisses; replaying a cleared level
          does not show it again until the level is passed a second time. A
          learner who wants to reread why `git rebase` moved those commits,
          three weeks later, looks for the level — so the link lives on the
          level, in the ledger, for as long as the clear does.

          A sibling of the row rather than a child of it: the whole row is
          already one anchor, and an anchor inside an anchor is not something
          a browser will render. It sits over the XP, which on a cleared row
          is a number the learner has already banked. */}
      {cleared && (
        <a className="lvl-debrief" href={`?debrief=${encodeURIComponent(mission.id)}`}>
          {/* One sentence with a hidden half: the word on screen, then which
              level it belongs to for a reader who has only the link. Parts,
              because the two halves do not come in this order in Arabic. */}
          <Parts
            of={s.home.row.debrief(
              <span className="sr-only">
                {checkpoint
                  ? s.home.row.debriefForCheckpoint(mission.title)
                  : s.home.row.debriefFor(mission.level, mission.title)}
              </span>,
            )}
          />
        </a>
      )}
    </motion.li>
  );
}

export default LevelRow;
